<template>
  <form-layout
    title="Send for approval"
    save-label="Send"
    discard-label="Cancel"
    has-changes
    :is-saving="isLoading"
    @on-dismiss="dismiss"
    @on-save="save"
    @on-discard="dismiss"
  >
    <vs-form ref="sendForApprovalForm">
      <vs-autocomplete
        ref="autocompleteComponent"
        :items="items"
        label="Send email to the following users"
        :value="formData"
        multiselect
        required
        @input="updateFormData"
      >
        <template v-if="canCreateNewGuestRequester" #append-item="{ currentSearchValue }">
          <v-divider class="mb-2" />
          <v-list-item
            data-test="create-new-requester-tile"
            class="create-new-requester-tile"
            @click="handleCreateGuestRequesterClick(currentSearchValue)"
          >
            <v-list-item-content>
              <vs-text class="blue-color" bold>{{
                currentSearchValue ? `Create new user: ${currentSearchValue}` : 'Create new user'
              }}</vs-text>
            </v-list-item-content>
            <v-list-item-action>
              <v-icon color="var(--color-blue)">add</v-icon>
            </v-list-item-action>
          </v-list-item>
        </template>
      </vs-autocomplete>
    </vs-form>

    <template #bottom-sheet>
      <vs-bottom-sheet :is-visible="showBottomSheet">
        <job-new-guest-user-form
          title="Add new approver"
          :initial-name-value="localSearchValue"
          @user-email-created="(userEmail) => formData.push(userEmail)"
          @dismiss="showBottomSheet = false"
        />
      </vs-bottom-sheet>
    </template>

    <template #snack-bar-wrapper>
      <vs-snackbar v-model="isSnackbarVisible" type="error" sticky>
        {{ snackbarError }}
      </vs-snackbar>
    </template>
  </form-layout>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useStore } from 'vuex-composition-helpers'
import { watchOnce } from '@vueuse/core'
import User from '@/models/user'
import Job from '@/models/job'
import { submitJobForApprovalMutation } from '@/api/jobs'
import FormLayout from '@/components/common/FormLayout.vue'
import VsForm from '@/components/vision/VsForm.vue'
import VsAutocomplete from '@/components/vision/Inputs/VsAutocomplete/VsAutocomplete.vue'
import VsSnackbar from '@/components/vision/VsSnackbar.vue'
import VsText from '@/components/vision/VsText.vue'
import JobNewGuestUserForm from '@/components/jobs/JobNewGuestUserForm.vue'
import VsBottomSheet from '@/components/vision/VsBottomSheet.vue'
import { useHubs } from '@/common/useHubs'
import { useBusinessUnitPermissions } from '@/components/tickets/common/useBusinessUnitPermissions'
import validateEmail from '@/utils/email-validation'
import { useJobBottomSheetStatesStore } from '@/components/jobs/jobBottomSheetStatesStore'

const props = defineProps<{
  job: Job
}>()

const jobBottomSheetStatesStore = useJobBottomSheetStatesStore()

const {
  mutateAsync: submitForApproval,
  isLoading,
  error: submitForApprovalError,
} = submitJobForApprovalMutation()
const showBottomSheet = ref(false)
const autocompleteComponent = ref()
const sendForApprovalForm = ref()
const formData = ref<string[]>([])
const store = useStore()
const getUsersRelatedToBusinessUnit = computed<(buId: string | undefined) => User[]>(
  () => store.getters['users/getUsersRelatedToBusinessUnit'],
)
const getUserBySid = computed<(userSid: string) => User>(() => store.getters['users/getBySid'])
const { canCreateNewGuestRequester } = useBusinessUnitPermissions()
const requester = computed(() => getUserBySid.value(props.job.customerSid))
const isSnackbarVisible = ref(false)
const snackbarError = ref()
watchOnce(
  requester,
  (newVal) => {
    formData.value.push(newVal.email)
  },
  { immediate: true },
)

const { currentHub } = useHubs()
const items = computed(() =>
  getUsersRelatedToBusinessUnit.value(currentHub.value?.id).map((u) => ({
    text: u.name,
    value: u.email,
  })),
)

const emit = defineEmits(['submitted-for-approval'])
function dismiss() {
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    isSendingEmailForApprovalBottomSheetVisible: false,
  })
}

const updateFormData = (e: string[] | null | undefined) => {
  if (e) {
    formData.value = e.filter(Boolean)
  }
}
const save = async () => {
  try {
    if (!sendForApprovalForm.value.isValid()) {
      return
    }
    if (formData.value.length === 0) {
      snackbarError.value = 'You must choose at least one user'
      isSnackbarVisible.value = true
      return
    }
    const sanitizedEmails = formData.value.filter((email) => validateEmail(email))
    await submitForApproval({ uuid: props.job.uuid, emails: sanitizedEmails })
    emit('submitted-for-approval')
    dismiss()
  } catch (error) {
    snackbarError.value = submitForApprovalError.value
    isSnackbarVisible.value = true
  }
}
const localSearchValue = ref()
const handleCreateGuestRequesterClick = (currentSearchValue?: string | null) => {
  localSearchValue.value = currentSearchValue
  showBottomSheet.value = true
  autocompleteComponent.value.close()
}
</script>

<style scoped>
.blue-color {
  color: var(--color-blue);
}
</style>
